import { ContentTranslationOrigin } from '@today/types'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, SelectInput, TextInput } from './content-fields'
import type { ContentOption, Translate } from './content-types'

/** The "Other" language choice; its typed tag is `otherLocale`. */
export const OTHER_LANGUAGE = '__other'

export interface AddLanguageValuesView {
	locale: string
	otherLocale: string
	origin: ContentTranslationOrigin.MANUAL | ContentTranslationOrigin.IMPORT
	translatedFromId: string
}

/**
 * Props for {@link AddLanguageDialogView}.
 *
 * @property locales - Languages not yet present in the version.
 * @property sources - Localizations of the same version to translate from.
 */
export interface AddLanguageDialogViewProps {
	t: Translate
	isOpen: boolean
	values: AddLanguageValuesView
	locales: string[]
	sources: ContentOption[]
	errors: Record<string, string>
	formError: string | null
	isBusy: boolean
	onChange: (field: keyof AddLanguageValuesView, value: string) => void
	onConfirm: () => void
	onCancel: () => void
}

function LanguageFields({ props }: { props: AddLanguageDialogViewProps }): ReactElement {
	const { t, values, errors, onChange } = props
	return (
		<>
			<SelectInput
				label={t('addLanguage.locale')}
				value={values.locale}
				options={[
					...props.locales.map((l) => ({ id: l, label: l })),
					{ id: OTHER_LANGUAGE, label: t('form.otherLocale') },
				]}
				error={values.locale === OTHER_LANGUAGE ? undefined : errors.locale}
				onChange={(v) => onChange('locale', v)}
			/>
			{values.locale === OTHER_LANGUAGE ? (
				<TextInput
					label={t('form.otherLocaleTag')}
					value={values.otherLocale}
					error={errors.locale}
					onChange={(v) => onChange('otherLocale', v)}
				/>
			) : null}
			<SelectInput
				label={t('addLanguage.origin')}
				value={values.origin}
				options={[
					{ id: ContentTranslationOrigin.MANUAL, label: t('origin.manual') },
					{ id: ContentTranslationOrigin.IMPORT, label: t('origin.import') },
				]}
				onChange={(v) => onChange('origin', v)}
			/>
			<SelectInput
				label={t('addLanguage.translatedFrom')}
				value={values.translatedFromId}
				emptyLabel={t('addLanguage.none')}
				options={props.sources}
				onChange={(v) => onChange('translatedFromId', v)}
			/>
		</>
	)
}

/** Add a language to a version: locale, origin and the localization it is translated from (organism). */
export function AddLanguageDialogView(props: AddLanguageDialogViewProps): ReactElement {
	const { t } = props
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={t('editor.addLanguage')}
			labels={{ close: t('actions.close') }}
		>
			<div className="flex flex-col gap-3">
				<ErrorText message={props.formError} />
				<LanguageFields props={props} />
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy} onPress={props.onConfirm}>
						{t('addLanguage.add')}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{t('actions.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
